import { Ruler, Trash2, X, Info } from 'lucide-react';
import type { Measurement, CameraCalibration } from '../types';

interface MeasurePanelProps {
  isMobileView: boolean;
  measurements: Measurement[];
  calibration: CameraCalibration | null;
  setActivePanel: (panel: 'chat' | 'notes' | 'annotate' | 'measure' | null) => void;
  clearMeasurements: () => void;
}

export function MeasurePanel({
  isMobileView,
  measurements,
  calibration,
  setActivePanel,
  clearMeasurements
}: MeasurePanelProps) {
  return (
    <div
      data-panel="measure"
      className={`
        ${isMobileView
          ? 'absolute inset-x-0 bottom-0 rounded-t-3xl h-[70vh]'
          : 'absolute right-0 top-0 bottom-0 w-96 rounded-l-2xl'
        }
        bg-gray-900/95 backdrop-blur-md shadow-2xl z-50
        flex flex-col overflow-hidden border-l border-white/10
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Ruler className="w-5 h-5" />
          Measurements
        </h2>
        <button
          onClick={() => setActivePanel(null)}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Close panel"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Calibration Status */}
      {calibration && (
        <div className={`
          mx-4 mt-4 p-3 rounded-lg flex items-start gap-3
          ${calibration.isDefault ? 'bg-yellow-900/20 border border-yellow-500/30' : 'bg-green-900/20 border border-green-500/30'}
        `}>
          <Info className={`w-5 h-5 flex-shrink-0 mt-0.5 ${calibration.isDefault ? 'text-yellow-400' : 'text-green-400'}`} />
          <div className="text-sm">
            <p className={`font-medium ${calibration.isDefault ? 'text-yellow-400' : 'text-green-400'}`}>
              {calibration.isDefault ? 'Using Default Calibration' : 'Device Calibrated'}
            </p>
            <p className="text-gray-400 text-xs mt-1">
              {calibration.deviceModel} • FOV: {calibration.horizontalFovDegrees}°
            </p>
            {calibration.isDefault && (
              <p className="text-yellow-400/70 text-xs mt-1">
                Measurements will be approximate. For better accuracy, use a known device.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="p-4 space-y-3">
        <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
          <p className="text-sm text-blue-300 font-medium mb-1">
            📱 Download the App to Measure
          </p>
          <p className="text-xs text-blue-200/70">
            To use AR measurement features, download the Wlkthru guest app on your mobile device.
          </p>
        </div>
        <p className="text-xs text-gray-400">
          Once connected, guests can tap the ruler icon on their mobile toolbar to measure objects in their camera view.
        </p>
      </div>

      {/* Measurements List */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {measurements.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Ruler className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No measurements yet</p>
            <p className="text-xs mt-1">Select a tool to start measuring</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-400 uppercase tracking-wide">
                Results ({measurements.length})
              </p>
              <button
                onClick={clearMeasurements}
                className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" />
                Clear All
              </button>
            </div>

            {measurements.map((m, index) => (
              <div
                key={m.id}
                className={`rounded-lg p-3 border ${
                  m.initiatedBy === 'guest'
                    ? 'bg-green-900/10 border-green-500/30'
                    : 'bg-blue-900/10 border-blue-500/30'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold ${
                      m.initiatedBy === 'guest' ? 'text-green-400' : 'text-blue-400'
                    }`}>
                      {m.initiatedBy === 'guest' ? '📱 Guest' : '💻 Host'}
                    </span>
                    <span className="text-xs text-gray-400">
                      • #{measurements.length - index} • {m.type.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(m.timestamp).toLocaleTimeString()}
                  </span>
                </div>

                <div className="space-y-1">
                  <div className="text-2xl font-bold text-white">
                    {m.real_distance_inches?.toFixed(2)} <span className="text-sm font-normal text-gray-400">inches</span>
                  </div>
                  <div className="text-sm text-gray-400">
                    {m.real_distance_cm?.toFixed(2)} cm
                  </div>
                </div>

                {m.note && (
                  <div className="mt-2 text-xs text-yellow-400/70 bg-yellow-900/10 rounded p-2">
                    {m.note}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
